import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../../assets/scss/attendanceConfiguration.scss";
import Select from "react-select";
import { axiosPost } from "../../utils/AxiosApi";
import { URL } from "../../utils/Constants";
import swal from "sweetalert";
import { Spinner } from "reactstrap";
import ModalWindow from "../UI/ModalWindow";
import Switch from "@material-ui/core/Switch";
import Pagination from "../UI/Pagination";
import { usePagination } from "../../hooks/usePagination";
import NotFound from "../UI/NotFound";
import { Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";

export const attendanceTypeOptions = [
  {
    label: "Subject Wise",
    value: "subjectwise",
  },
  {
    label: "Class Wise",
    value: "classwise",
  },
];

const AttendanceConfiguration = () => {
  const [formData, setFormData] = useState({
    classroomId: [],
    attendanceType: "",
  });
  const [editedData, setEditedData] = useState({
    attendanceType: "",
  });
  const [addModal, setAddModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deletedRow, setDeletedRow] = useState(null);
  const [editRowId, setEditRowId] = useState(null);
  const [yearLevels, setYearLevels] = useState([]);
  const [configuration, setConfiguration] = useState([]);
  const [totalRecordsCount, setTotalRecordsCount] = useState(0);
  const [loading, setLoading] = useState({
    read: false,
    insert: false,
    delete: false,
  });

  const {
    currentPage,
    pageSize,
    totalPages,
    setCurrentPage,
    onNextPage,
    onPrevPage,
    onPageChange,
  } = usePagination(totalRecordsCount);

  const yearLevelsOptions = useMemo(() => {
    if (yearLevels?.length === 0) return [];
    return yearLevels?.map((item) => ({
      label: `Class ${item?.yearLevelName} - ${item?.name}`,
      value: item?.id,
      yearLevelId: item?.yearLevelId,
    }));
  }, [yearLevels]);

  const yearLevelConfig = useMemo(() => {
    if (yearLevels?.length === 0) return {};

    const map = {};
    yearLevels.forEach((item) => {
      map[item.id] = item;
    });

    return map;
  }, [yearLevels]);

  const getAttendanceConfiguration = useCallback(
    (page) => {
      setLoading((prev) => ({ ...prev, read: true }));
      axiosPost(
        URL.getAttendanceConfig,
        {
          limit: pageSize,
          pageSize: page - 1,
        },
        (response) => {
          if (response?.status === 200) {
            const data = response?.data?.data;
            const processedData = data?.map((item) => ({
              id: item?.id,
              classroomId: item?.classroomId,
              yearLevelId: item?.yearLevelId,
              attendanceType: item?.attendanceType,
              sms: item?.sms ?? false,
            }));
            setConfiguration(processedData);
            setTotalRecordsCount(
              response?.data?.totalRecordsCount ?? processedData?.length,
            );
          }
          setLoading((prev) => ({ ...prev, read: false }));
        },
        (error) => {
          setLoading((prev) => ({ ...prev, read: false }));
        },
      );
    },
    [pageSize],
  );

  const handleChange = (option, field) => {
    if (field === "classroomId") {
      const classroomIds = option?.map((item) => item?.value);
      setFormData((prev) => ({
        ...prev,
        [field]: classroomIds ?? [],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: option,
      }));
    }
  };

  const handleEdit = async (data) => {
    const params = [
      {
        ...data,
        attendanceType: editedData.attendanceType,
        isUpdated: true,
      },
    ];

    try {
      await updateAttendanceConfig(params);
      setEditRowId(null);
      setEditedData({
        attendanceType: "",
      });
    } catch (error) {
      console.error("Edit Failed: ", error);
    }
  };

  const getClassrooms = () => {
    axiosPost(URL.selectClassroom, { recruimentId: null }, (response) => {
      if (response?.status === 200) {
        setYearLevels(response?.data?.data);
      }
    });
  };

  const renderTable = (data) => {
    return (
      <div className="attendance-table-wrapper">
        <table style={{ margin: "0" }} className="attendance-table">
          <thead>
            <tr>
              <th>S.No.</th>
              <th>Class Name</th>
              <th>Attendance Type</th>
              <th>
                <Tooltip
                  title={
                    "SMS credits will be consumed if this feature is activated."
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      width: "fit-content",
                    }}
                  >
                    SMS
                    <FontAwesomeIcon icon={faCircleInfo} fontSize={14} />
                  </div>
                </Tooltip>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading?.read ? (
              <>
                <tr>
                  <td colSpan={5}>
                    <div className="attendance-spinner">
                      <div className="loader">
                        <Spinner size={"md"} color="primary"></Spinner>
                      </div>
                      <div style={{ fontSize: "13px", marginTop: "15px" }}>
                        Please wait !! Processing.....
                      </div>
                    </div>
                  </td>
                </tr>
              </>
            ) : data?.length > 0 ? (
              data?.map((item, index) => {
                const yearLevelName = yearLevelsOptions?.find(
                  (opt) => opt.value === item?.classroomId,
                );

                const editable = item?.id === editRowId;

                return (
                  <tr key={item?.id}>
                    <td>{(currentPage - 1) * pageSize + index + 1}</td>
                    <td>{yearLevelName?.label}</td>
                    <td>
                      {editable ? (
                        <Select
                          value={
                            attendanceTypeOptions?.find(
                              (opt) =>
                                opt?.value ===
                                (editedData.attendanceType ||
                                  item?.attendanceType),
                            ) || null
                          }
                          onChange={(option) =>
                            setEditedData((prev) => ({
                              ...prev,
                              attendanceType: option?.value,
                            }))
                          }
                          options={attendanceTypeOptions}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              width: "200px",
                            }),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                          }}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      ) : (
                        <span
                          className="arr-badge"
                          style={{
                            background:
                              item?.attendanceType === "subjectwise"
                                ? "#FCEBEB"
                                : "#E6F1FB",
                            color:
                              item?.attendanceType === "subjectwise"
                                ? "#791F1F"
                                : "#0C447C",
                            fontSize: "12px",
                            borderRadius: "24px",
                            padding: "4px 14px",
                            textAlign: "center",
                            width: "fit-content",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item?.attendanceType}
                        </span>
                      )}
                    </td>
                    <td>
                      <Switch
                        checked={item?.sms}
                        name={"sms"}
                        onClick={(e) => {
                          setConfiguration((prev) => {
                            const updated = prev?.map((config, i) =>
                              i === index
                                ? { ...config, sms: !config?.sms }
                                : config,
                            );

                            return updated;
                          });
                        }}
                        inputProps={{ "aria-label": "controlled" }}
                      />
                    </td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {item?.id && editable && (
                          <>
                            <button
                              onClick={() => {
                                setEditRowId(null);
                                setEditedData({
                                  attendanceType: "",
                                });
                              }}
                              className="secondary-btn"
                              style={{ padding: "6px 10px" }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleEdit(item)}
                              className="primary-btn"
                              style={{ padding: "6px 10px" }}
                              disabled={loading.insert}
                            >
                              {loading.insert ? (
                                <Spinner size={"sm"} color="white" />
                              ) : (
                                "Save"
                              )}
                            </button>
                          </>
                        )}

                        {item?.id && !editable && (
                          <button
                            onClick={() => {
                              setEditRowId(item?.id);
                              setEditedData({
                                attendanceType: item?.attendanceType,
                              });
                            }}
                            style={{ padding: "6px 10px" }}
                            className="primary-btn"
                          >
                            Edit
                          </button>
                        )}

                        {!editable && (
                          <button
                            onClick={() => {
                              setDeleteModal(true);
                              setDeletedRow(item);
                              return;
                            }}
                            style={{ padding: "6px 10px" }}
                            className="danger-btn"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={5} style={{ textAlign: "center" }}>
                  <NotFound />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const updateAttendanceConfig = (params) => {
    setLoading((prev) => ({
      ...prev,
      insert: true,
    }));

    return new Promise((resolve, reject) => {
      axiosPost(
        URL.updateAttendanceConfig,
        params,
        (response) => {
          if (response?.status === 200) {
            swal("Success", "Successfully saved attendance configuration");
            resolve(response);
            getAttendanceConfiguration(currentPage);
          } else {
            reject(response);
          }
          setLoading((prev) => ({ ...prev, insert: false }));
        },
        (error) => {
          swal(
            "Oops",
            error?.response?.data?.message ||
              "Failed to save attendance configuration",
          );
          setLoading((prev) => ({ ...prev, insert: false }));
          reject(error);
        },
      );
    });
  };

  const handleDeleteAttendanceConfig = () => {
    setLoading((prev) => ({ ...prev, delete: true }));

    axiosPost(
      URL.deleteAttendanceConfig,
      {
        yearLevelId: deletedRow?.yearLevelId,
        classroomId: deletedRow?.classroomId,
      },
      (response) => {
        if (response?.status === 200) {
          swal("Success", "Successfully deleted attendance configuration");
          setDeleteModal(false);
          setDeletedRow(null);

          //pagination logic
          const updatedTotalItems = totalRecordsCount - 1;
          const updatedTotalPages = Math.ceil(updatedTotalItems / pageSize);
          let page = currentPage;
          if (currentPage > 1 && currentPage > updatedTotalPages) {
            page = currentPage - 1;
            setCurrentPage(page);
          }
          getAttendanceConfiguration(page);
        }
        setLoading((prev) => ({ ...prev, delete: false }));
      },
      (error) => {
        swal(
          "Oops",
          error?.response?.data?.message ||
            "Failed to delete attendance configuration",
        );
        setLoading((prev) => ({ ...prev, delete: false }));
      },
    );
  };

  const handleSaveConfiguration = async () => {
    if (formData?.classroomId?.length === 0) {
      swal("Oops", "Please select at least one class");
      return;
    }

    if (formData?.attendanceType === "") {
      swal("Oops", "Please select attendance type");
      return;
    }

    const newData = formData?.classroomId?.map((item) => {
      const yearLevelId = yearLevelConfig[item]?.yearLevelId;
      return {
        classroomId: item,
        attendanceType: formData.attendanceType,
        sms: false,
        yearLevelId,
      };
    });

    try {
      await updateAttendanceConfig(newData);
      setEditRowId(null);
      setEditedData({
        attendanceType: "",
      });
      setFormData({
        classroomId: [],
        attendanceType: "",
      });
      setAddModal(false);
    } catch (error) {
      console.error("Save Failed: ", error);
    }
  };

  useEffect(() => {
    getClassrooms();
  }, []);

  useEffect(() => {
    getAttendanceConfiguration(currentPage);
  }, [currentPage, getAttendanceConfiguration]);

  return (
    <>
      <div
        style={{ height: "80vh", overflowY: "auto" }}
        className="attendance-configuration"
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            justifyContent: "end",
            marginBottom: "12px",
          }}
        >
          <button
            onClick={() => {
              setConfiguration((prev) => {
                return prev?.map((item) => ({ ...item, sms: true }));
              });
            }}
            style={{ padding: "8px 20px" }}
            className="secondary-btn"
            disabled={configuration?.length === 0}
          >
            Bulk Enable SMS
          </button>
          <button
            onClick={() => setAddModal(true)}
            style={{ padding: "8px 20px" }}
            className="primary-btn"
          >
            Add Configuration
          </button>
        </div>

        {renderTable(configuration)}

        <div
          className="modern-pagination"
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "48px",
          }}
        >
          <Pagination
            type={"modern"}
            pageSize={pageSize}
            currentPage={currentPage}
            itemsCount={totalRecordsCount}
            onPageChange={onPageChange}
            nextPage={onNextPage}
            previousPage={onPrevPage}
          />
        </div>
      </div>

      <ModalWindow
        modal={addModal}
        size={"md"}
        toggleModal={() => setAddModal(false)}
        modalHeader={"Add Configuration"}
        modalBody={
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <div style={{ width: "100%" }} className="form-input">
              <label htmlFor="class">Class</label>
              <Select
                isMulti
                value={
                  yearLevelsOptions?.filter((opt) =>
                    formData?.classroomId?.includes(opt.value),
                  ) || null
                }
                onChange={(options) => handleChange(options, "classroomId")}
                options={yearLevelsOptions?.filter(
                  (opt) =>
                    !configuration?.some((c) => c.classroomId === opt?.value),
                )}
                styles={{
                  control: (base) => ({ ...base, width: "100%" }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
                menuPortalTarget={document.body}
              />
            </div>

            <div style={{ width: "100%" }} className="form-input">
              <label htmlFor="attendanceType">Attendance Type</label>
              <Select
                value={
                  attendanceTypeOptions?.find(
                    (opt) => opt?.value === formData.attendanceType,
                  ) || null
                }
                onChange={(option) =>
                  handleChange(option?.value, "attendanceType")
                }
                options={attendanceTypeOptions}
                styles={{
                  control: (base) => ({ ...base, width: "100%" }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div style={{ display: "flex", justifyContent: "end" }}>
              <button
                onClick={handleSaveConfiguration}
                style={{
                  padding: "8px 20px",
                  width: "fit-content",
                }}
                className="primary-btn"
                disabled={loading.insert}
              >
                {loading?.insert ? (
                  <Spinner color="white" size={"sm"} />
                ) : (
                  "Save Configuration"
                )}
              </button>
            </div>
          </div>
        }
      />

      <ModalWindow
        modal={deleteModal}
        toggleModal={() => setDeleteModal(false)}
        modalHeader={"Delete Record"}
        modalBody={
          <div>
            <p>Once deleted, you won't be able to recover it.</p>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
                gap: "8px",
              }}
            >
              <button
                onClick={() => {
                  setDeleteModal(false);
                  setDeletedRow(null);
                }}
                disabled={loading.delete}
                className="secondary-btn"
                style={{ padding: "6px 10px" }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleDeleteAttendanceConfig();
                }}
                className="danger-btn"
                disabled={loading.delete}
                style={{ padding: "6px 10px" }}
              >
                {loading.delete ? (
                  <Spinner size={"sm"} color="white" />
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        }
      />
    </>
  );
};

export default AttendanceConfiguration;
